import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { FileText, Loader2, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { cn } from 'src/lib/utils';
import { Actions as PUBLIC } from '../../store/actions/publicAgreementActions';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { stripeCurrencyToSymbol } from '../utils/stripeCurrency';
import { formatMajorMoney, readTier } from '../utils/stripeBanding';
import {
  filterPublicLegalDocuments,
  formatCyfContractingEntity,
  resolvePublicEndClientBillingDisplay,
  formatIndexationPolicy,
  formatLegalDocumentTitle,
} from '../Modals/agreementsUtils';

const { apiURL: apiBase } = require('../../store/actions/apiURL');

const TABLE_MD_COMPONENTS = {
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto -mx-1 my-4">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
};

const markdownProseClasses = cn(
  'text-sm leading-relaxed text-foreground',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:my-3 [&_p]:leading-relaxed',
  '[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-5 [&_h1]:mb-2',
  '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-3',
  '[&_li]:my-1.5 [&_li]:leading-relaxed',
  '[&_strong]:font-semibold',
  '[&_em]:italic',
  '[&_a]:text-primary [&_a]:underline hover:[&_a]:no-underline',
  '[&_code]:bg-muted [&_code]:text-foreground [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.9em]',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:my-3',
  '[&_hr]:my-5 [&_hr]:border-border',
  '[&_th]:border [&_th]:border-border [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-muted/40',
  '[&_td]:border [&_td]:border-border [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:align-top',
);

const PublicAgreementPage = ({
  getPublicAgreement,
  acceptPublicAgreement,
  createPublicCheckoutSession,
  publicPayload,
  publicLoading,
  publicError,
  clearPublicAgreement,
}) => {
  const [token] = useState(() => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const i = parts.indexOf('agreement');
    return i >= 0 && parts[i + 1] ? parts[i + 1] : '';
  });
  const [docChecks, setDocChecks] = useState({});
  const [docText, setDocText] = useState({});
  const [expandedKind, setExpandedKind] = useState(null);
  const [signer, setSigner] = useState({ name: '', email: '', title: '' });
  const [master, setMaster] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [expandedTiers, setExpandedTiers] = useState({});

  useEffect(() => {
    if (!token) return;
    (async () => {
      const data = await getPublicAgreement(token);
      if (data) {
        setSigner({
          name: [data.customer?.firstName, data.customer?.lastName].filter(Boolean).join(' ') || '',
          email: data.customer?.email || '',
          title: '',
        });
      }
    })();
    return () => clearPublicAgreement();
  }, [token, getPublicAgreement, clearPublicAgreement]);

  const legalDocs = useMemo(
    () => filterPublicLegalDocuments(publicPayload?.legalDocuments, publicPayload?.type),
    [publicPayload?.legalDocuments, publicPayload?.type],
  );
  const allChecked = useMemo(() => {
    if (!legalDocs.length) return false;
    return legalDocs.every((d) => docChecks[d.documentKind + '@' + d.versionLabel]);
  }, [legalDocs, docChecks]);

  const openMarkdown = async (doc) => {
    const k = doc.documentKind + '@' + doc.versionLabel;
    setExpandedKind(k);
    if (docText[k]) return;
    let path = doc.markdownPath || '';
    if (path && !path.startsWith('http')) {
      if (!path.startsWith('/')) path = '/' + path;
      const url = apiBase + path;
      const res = await fetch(url, { headers: { Accept: 'text/markdown' } });
      if (res.ok) {
        const t = await res.text();
        setDocText((prev) => ({ ...prev, [k]: t }));
      } else {
        setDocText((prev) => ({
          ...prev,
          [k]: 'Unable to load document. Please use the order form PDF for reference.',
        }));
      }
    }
  };

  const onAccept = async () => {
    if (!allChecked || !master || !signer.name || !signer.email) return;
    setLoadErr(null);
    const documents = legalDocs.map((d) => ({
      documentKind: d.documentKind,
      versionLabel: d.versionLabel,
      artifactSha256: d.sha256,
    }));
    const r = await acceptPublicAgreement(token, { documents, signer });
    if (!r?.ok) {
      if (r?.code === 'snapshot_validation_failed') {
        setLoadErr('Documents were updated. The page was refreshed—please read and accept the latest versions.');
        await getPublicAgreement(token);
        setDocChecks({});
        setMaster(false);
      } else {
        setLoadErr(r?.data?.code || r?.message || 'Acceptance failed');
      }
      return;
    }
    const d = r.data;
    setSubmitted(d);
    if (d?.nextAction === 'checkout_url' && d?.checkoutUrl) {
      window.location.href = d.checkoutUrl;
      return;
    }
    if (d?.nextAction === 'checkout_url' && !d?.checkoutUrl) {
      const s = await createPublicCheckoutSession(token);
      if (s?.ok && s.data?.url) {
        window.location.href = s.data.url;
        return;
      }
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <p className="text-muted-foreground">Invalid agreement link.</p>
      </div>
    );
  }

  if (publicError === 'not_found' && !publicPayload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">This agreement link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (publicLoading && !publicPayload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (submitted && (submitted.nextAction === 'awaiting_invoice' || (submitted.checkoutUrl === null && submitted.nextAction !== 'checkout_url'))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle>Agreement accepted</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p>Thank you. You will be invoiced according to the terms. Please log in to the app to continue when your account is ready.</p>
            <Button onClick={() => (window.location.href = '/')}>Go to sign in</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const p = publicPayload;
  if (!p) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <p className="text-muted-foreground">Unable to load agreement.</p>
      </div>
    );
  }

  const lineItems = p.lineItems || [];
  const endClientBillingDisplay = resolvePublicEndClientBillingDisplay(p);
  const term = p.term || {};
  const idx = p.indexationPolicy;
  const indexationSummary = formatIndexationPolicy(idx);
  const contractingEntity = formatCyfContractingEntity(p.cyfContractingEntity);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold mb-2">Review & accept agreement</h1>
      <p className="text-sm text-muted-foreground mb-6">Please review the order form, legal documents, and sign below.</p>

      {loadErr && (
        <Alert variant="warning" className="mb-4">
          <AlertDescription>{loadErr}</AlertDescription>
        </Alert>
      )}

      {p.tokenExpiresAt && (
        <p className="text-xs text-muted-foreground mb-4">Link valid until: {format(parseISO(p.tokenExpiresAt), 'PPP p')}</p>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText size={18} strokeWidth={1.5} />
            {p.type} — {p.customer?.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {p.orderFormPdfUrl && (
            <div className="h-[480px] border border-border rounded-md overflow-hidden">
              <iframe title="Order form" src={p.orderFormPdfUrl} className="w-full h-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {contractingEntity && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Contracting party</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="font-semibold">{contractingEntity.title}</p>
            {contractingEntity.lines.map((line) => (
              <p key={line} className="text-muted-foreground">
                {line}
              </p>
            ))}
            {contractingEntity.isDefault && (
              <p className="text-xs text-muted-foreground italic">
                Default jurisdiction (no specific row matched).
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {lineItems.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Line items</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((row, i) => {
                  const sym = stripeCurrencyToSymbol(row.currency);
                  const isTiered = row.billingScheme === 'tiered';
                  // Public payload sends MAJOR units on unitAmount/flatAmount/bandingTier.
                  const amountText = isTiered
                    ? formatMajorMoney(row.currency, row.flatAmount ?? row.bandingTier?.flatAmount, stripeCurrencyToSymbol)
                    : formatMajorMoney(row.currency, row.unitAmount, stripeCurrencyToSymbol);
                  return (
                    <React.Fragment key={i}>
                      <TableRow>
                        <TableCell className="font-medium">
                          {row.description || '—'}
                          {row.billingFrequency && (
                            <span className="text-xs text-muted-foreground"> · {row.billingFrequency}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.kind}
                          {isTiered && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-primary/10">
                              <Tag size={10} strokeWidth={1.5} />
                              banded
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{row.quantity}</TableCell>
                        <TableCell className="text-right">{amountText}</TableCell>
                      </TableRow>
                      {isTiered && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={4} className="text-xs">
                            <div className="space-y-1">
                              {row.billingUnitLabel && (
                                <div>
                                  <span className="text-muted-foreground">{row.billingUnitLabel}: </span>
                                  <span className="font-semibold">
                                    {row.billingUnit === 'revenue' && row.bandingQuantity != null
                                      ? `${sym}${Number(row.bandingQuantity).toLocaleString()}`
                                      : row.bandingQuantity != null
                                        ? Number(row.bandingQuantity).toLocaleString()
                                        : '—'}
                                  </span>
                                </div>
                              )}
                              {row.bandingTier && (
                                <div>
                                  <span className="text-muted-foreground">You'll be billed at: </span>
                                  <span className="font-semibold">
                                    {formatMajorMoney(row.currency, row.bandingTier.flatAmount, stripeCurrencyToSymbol)}
                                  </span>
                                  {row.billingFrequency && (
                                    <span className="text-muted-foreground"> per {row.billingFrequency}</span>
                                  )}
                                  <span className="text-muted-foreground">
                                    {' '}(tier {Number(row.bandingTier.index ?? 0) + 1}
                                    {row.bandingTier.upTo === 'inf'
                                      ? ', no upper limit'
                                      : row.bandingTier.upTo != null
                                        ? `, up to ${row.billingUnit === 'revenue' ? sym : ''}${Number(row.bandingTier.upTo).toLocaleString()}`
                                        : ''}
                                    )
                                  </span>
                                </div>
                              )}
                              {Array.isArray(row.tiers) && row.tiers.length > 1 && (
                                <div>
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto p-0 text-xs"
                                    onClick={() =>
                                      setExpandedTiers((prev) => ({
                                        ...prev,
                                        [i]: !prev[i],
                                      }))
                                    }
                                  >
                                    {expandedTiers[i] ? (
                                      <>
                                        <ChevronUp size={12} strokeWidth={1.5} className="mr-1" />
                                        Hide all bands
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown size={12} strokeWidth={1.5} className="mr-1" />
                                        See all {row.tiers.length} bands
                                      </>
                                    )}
                                  </Button>
                                  {expandedTiers[i] && (
                                    <div className="mt-2 overflow-x-auto">
                                      <table className="w-full text-xs border-collapse">
                                        <thead>
                                          <tr className="text-muted-foreground">
                                            <th className="text-left p-1 font-normal">Up to</th>
                                            <th className="text-right p-1 font-normal">Flat</th>
                                            <th className="text-right p-1 font-normal">Per unit</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.tiers.map((t, ti) => {
                                            const norm = readTier(t);
                                            const isMatch = (row.bandingTier?.index ?? -1) === ti;
                                            const upToTxt =
                                              norm.upToRaw === 'inf' || norm.upToRaw === null
                                                ? '∞'
                                                : `${row.billingUnit === 'revenue' ? sym : ''}${Number(norm.upToRaw).toLocaleString()}`;
                                            return (
                                              <tr
                                                key={ti}
                                                className={isMatch ? 'bg-primary/10 font-semibold' : ''}
                                              >
                                                <td className="p-1">
                                                  {isMatch ? '→ ' : ''}
                                                  {upToTxt}
                                                </td>
                                                <td className="p-1 text-right">
                                                  {formatMajorMoney(row.currency, norm.flatAmount, stripeCurrencyToSymbol)}
                                                </td>
                                                <td className="p-1 text-right">
                                                  {formatMajorMoney(row.currency, norm.unitAmount, stripeCurrencyToSymbol)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-2">
              {endClientBillingDisplay
                ? 'These amounts are for your platform licence only. How your clients are billed is shown below.'
                : 'Amounts are as agreed on the order form (see PDF).'}
            </p>
          </CardContent>
        </Card>
      )}

      {endClientBillingDisplay && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">How your clients are billed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {endClientBillingDisplay.headline.main && (
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-4">
                <p className="text-2xl font-bold text-foreground leading-tight">
                  {endClientBillingDisplay.headline.main}
                </p>
                {endClientBillingDisplay.headline.sub && (
                  <p className="text-sm text-muted-foreground mt-1">{endClientBillingDisplay.headline.sub}</p>
                )}
              </div>
            )}
            {endClientBillingDisplay.paragraphs.map((paragraph) => (
              <p key={paragraph} className="text-sm text-foreground leading-relaxed">
                {paragraph}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Term & indexation</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          {term.commitmentEndDate && (
            <p>
              <span className="text-muted-foreground">Commitment ends: </span>
              <span className="font-semibold">{format(parseISO(term.commitmentEndDate), 'PPP')}</span>
            </p>
          )}
          {term.noticePeriodDays != null && <p>Notice period: {term.noticePeriodDays} days</p>}
          {indexationSummary && (
            <p>
              <span className="text-muted-foreground">Indexation: </span>
              {indexationSummary}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Legal documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {legalDocs.map((d) => {
            const k = d.documentKind + '@' + d.versionLabel;
            return (
              <div key={k} className="border border-border rounded-md p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">
                      {formatLegalDocumentTitle(d)}
                      <span className="text-muted-foreground font-normal"> · v{d.versionLabel}</span>
                    </p>
                    <Button variant="link" className="h-auto p-0" type="button" onClick={() => openMarkdown(d)}>
                      Read full text
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={k}
                      checked={!!docChecks[k]}
                      onCheckedChange={(c) => setDocChecks((prev) => ({ ...prev, [k]: !!c }))}
                    />
                    <Label htmlFor={k} className="text-sm">I have read and agree</Label>
                  </div>
                </div>
                {expandedKind === k && docText[k] && (
                  <div className="mt-3 min-h-[40vh] max-h-[min(65vh,42rem)] overflow-y-auto rounded-md border border-border bg-muted/30 p-4">
                    <ReactMarkdown
                      remarkPlugins={[gfm]}
                      components={TABLE_MD_COMPONENTS}
                      className={markdownProseClasses}
                    >
                      {docText[k]}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Signatory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-w-md">
          <div>
            <Label htmlFor="sig-name">Full name</Label>
            <Input
              id="sig-name"
              value={signer.name}
              onChange={(e) => setSigner((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="sig-email">Email</Label>
            <Input
              id="sig-email"
              type="email"
              value={signer.email}
              onChange={(e) => setSigner((s) => ({ ...s, email: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="sig-title">Title</Label>
            <Input
              id="sig-title"
              value={signer.title}
              onChange={(e) => setSigner((s) => ({ ...s, title: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-start gap-2 mb-4">
        <Checkbox id="master" checked={master} onCheckedChange={setMaster} />
        <Label htmlFor="master" className="text-sm leading-relaxed">
          I have read and agree to all of the above legal documents and the order form.
        </Label>
      </div>

      <Button
        size="lg"
        className="w-full sm:w-auto"
        disabled={!allChecked || !master || publicLoading}
        onClick={onAccept}
        loading={publicLoading}
      >
        Accept and continue
      </Button>
    </div>
  );
};

const mapState = (s) => ({
  publicPayload: s.publicAgreement?.payload,
  publicLoading: s.publicAgreement?.loading,
  publicError: s.publicAgreement?.error,
});

const mapDispatch = (d) => ({
  getPublicAgreement: (t) => d(PUBLIC.getPublicAgreement(t)),
  acceptPublicAgreement: (t, b) => d(PUBLIC.acceptPublicAgreement(t, b)),
  createPublicCheckoutSession: (t) => d(PUBLIC.createPublicCheckoutSession(t)),
  clearPublicAgreement: () => d(PUBLIC.clearPublicAgreement()),
});

export default connect(mapState, mapDispatch)(PublicAgreementPage);
